import {
  getDownloadURL,
  getStorage,
  listAll,
  ref,
  getMetadata,
} from "firebase/storage";
import { firebaseApp } from "../../firebase/firebaseApp";
import { metadataDirPath } from "../patient/patientStoragePath";

/**
 * Downloads and returns the newest metadata JSON for a given uid + patient context.
 *
 * Rules:
 * - id blank => reads from user_metadata/{uid}
 * - id set   => reads from user_metadata/{uid}/{id}
 *
 * @param {string} uid
 * @param {string|null|undefined} [id]
 * @returns {Promise<any|null>}
 */
export async function fetchLatestMetadata(uid, id) {
  if (!uid) throw new Error("fetchLatestMetadata: uid is required");

  const storage = getStorage(firebaseApp);
  const dirRef = ref(storage, metadataDirPath(uid, id));

  const { items } = await listAll(dirRef);
  if (!items.length) return null;

  // Pick newest by timeCreated (or updated)
  let newest = null; // { ref, timeCreatedMs }

  for (const itemRef of items) {
    // eslint-disable-next-line no-await-in-loop
    const meta = await getMetadata(itemRef);
    const t = Date.parse(meta.timeCreated || meta.updated || "");
    if (!Number.isFinite(t)) continue;

    if (!newest || t > newest.timeCreatedMs) {
      newest = { ref: itemRef, timeCreatedMs: t };
    }
  }

  const newestRef = newest?.ref ?? items[items.length - 1];

  const url = await getDownloadURL(newestRef);
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error("Download failed");

  const json = await res.json();
  return json.responses ?? json;
}
